// AdminSystemSettingsPage — F9.5 WAVE 2E · list + toggle for system_settings
// (kill-switches). Admin-only. Writes go through the existing
// PATCH /api/admin/system-settings/{key} endpoint which already enforces admin
// role, audit log, and server-side type coercion.
import React, { useEffect, useState, useCallback } from "react";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, History, Loader2 } from "lucide-react";

const CRITICAL_KEYS = [
    "LANDING_V3_ENABLED",
    "LANDING_V3_IS_ROOT",
    "HEADER_V3_ENABLED",
    "DASHBOARD_V3_ENABLED",
    "EMERGENT_AUTH_ENABLED",
    "DIRECT_AUTH_ENABLED",
];

export default function AdminSystemSettingsPage() {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [recent, setRecent] = useState([]);
    const [filter, setFilter] = useState("");
    const [confirmKey, setConfirmKey] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await apiFetch("/admin/system-settings");
            setSettings(Array.isArray(list) ? list : list?.settings || []);
            const audit = await apiFetch("/admin/audit-log?limit=10").catch(() => null);
            setRecent(Array.isArray(audit) ? audit : audit?.entries || []);
        } catch (e) {
            toast.error("Impossibile caricare system_settings: " + (e?.message || e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doToggle = async (row, newValue) => {
        setSavingKey(row.key);
        try {
            await apiFetch(`/admin/system-settings/${row.key}`, {
                method: "PATCH",
                body: JSON.stringify({ value: newValue, reason: "admin-ui toggle" }),
            });
            toast.success(`${row.key} → ${String(newValue)}`);
            await load();
        } catch (e) {
            toast.error(`Flip failed: ${e?.message || e}`);
        } finally {
            setSavingKey(null);
            setConfirmKey(null);
        }
    };

    const onToggleClick = (row) => {
        const newValue = !(row.value === true || row.enabled === true);
        if (CRITICAL_KEYS.includes(row.key)) {
            setConfirmKey({ row, newValue });
            return;
        }
        doToggle(row, newValue);
    };

    const filtered = settings.filter((s) =>
        !filter || s.key.toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div className="min-h-screen" style={{ backgroundColor: "#0A0A0A", color: "#FFFFFF" }}>
            <div className="max-w-6xl mx-auto px-6 py-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                         style={{ background: "rgba(255, 255, 255,0.15)" }}>
                        <ShieldCheck className="w-5 h-5" style={{ color: "#FFFFFF" }} />
                    </div>
                    <h1 className="text-2xl font-bold">Kill-Switches &amp; System Settings</h1>
                </div>
                <p className="text-sm text-[#CCCCCC] mb-6">
                    Gestione live dei feature flag. Le modifiche sono registrate in <code>admin_audit_log</code>.
                </p>

                <div className="flex items-center gap-3 mb-4">
                    <input
                        type="search"
                        placeholder="Cerca per key..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="flex-1 bg-[#111111] border border-white/5 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#999999] focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="admin-system-settings-filter"
                    />
                    <button
                        onClick={load}
                        className="px-4 py-2 rounded-lg text-sm bg-white/[0.06] hover:bg-white/[0.1] text-white"
                    >
                        Ricarica
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-[#CCCCCC] py-12 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Caricamento…</span>
                    </div>
                ) : (
                    <div className="rounded-xl border border-white/5 overflow-hidden" style={{ backgroundColor: "#111111" }}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-[#CCCCCC] bg-white/[0.03]">
                                    <th className="px-4 py-2.5">Key</th>
                                    <th className="px-4 py-2.5">Tipo</th>
                                    <th className="px-4 py-2.5">Valore</th>
                                    <th className="px-4 py-2.5 text-right">Azione</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((row) => {
                                    const isBool = typeof row.value === "boolean" || typeof row.enabled === "boolean";
                                    const current = row.value ?? row.enabled;
                                    const isCritical = CRITICAL_KEYS.includes(row.key);
                                    return (
                                        <tr key={row.key} className="border-t border-white/5 hover:bg-white/[0.02]">
                                            <td className="px-4 py-3 font-mono text-xs text-white">
                                                {row.key}
                                                {isCritical && (
                                                    <span className="ml-2 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
                                                          style={{ backgroundColor: "rgba(250, 250, 250,0.15)", color: "#FAFAFA" }}>
                                                        Critica
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-[#CCCCCC] text-xs">
                                                {isBool ? "bool" : typeof current}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs">
                                                <span className={current === true ? "text-[#CCCCCC]" : "text-[#CCCCCC]"}>
                                                    {String(current ?? "null")}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {isBool ? (
                                                    <button
                                                        onClick={() => onToggleClick(row)}
                                                        disabled={savingKey === row.key}
                                                        data-testid={`admin-system-setting-toggle-${row.key}`}
                                                        className={
                                                            "relative inline-flex h-5 w-10 items-center rounded-full transition " +
                                                            (current ? "" : "opacity-70") +
                                                            (savingKey === row.key ? " animate-pulse" : "")
                                                        }
                                                        style={{ backgroundColor: current ? "#FFFFFF" : "#1A1A1A", border: "1px solid rgba(255,255,255,0.08)" }}
                                                    >
                                                        <span
                                                            className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition shadow"
                                                            style={{ transform: current ? "translateX(22px)" : "translateX(2px)" }}
                                                        />
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-[#999999]">read-only</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filtered.length === 0 && (
                            <div className="text-center text-sm text-[#CCCCCC] py-6">Nessun risultato</div>
                        )}
                    </div>
                )}

                {recent.length > 0 && (
                    <div className="mt-8">
                        <div className="flex items-center gap-2 mb-3">
                            <History className="w-4 h-4" style={{ color: "#CCCCCC" }} />
                            <h2 className="text-sm font-semibold text-[#E5E5E5]">Ultimi 10 flip</h2>
                        </div>
                        <ul className="space-y-1.5">
                            {recent.slice(0, 10).map((e, i) => (
                                <li key={e.id || i} className="text-xs text-[#CCCCCC] font-mono flex items-center gap-2">
                                    <span className="text-[#999999]">{(e.ts || e.created_at || "").slice(0, 19)}</span>
                                    <span className="text-white">{e.key || e.target_key || "?"}</span>
                                    <span>→</span>
                                    <span style={{ color: "#FFFFFF" }}>{String(e.new_value ?? e.value ?? "?")}</span>
                                    <span className="text-[#999999]">by {e.actor_email || e.actor_id || "?"}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Confirmation modal for critical keys */}
            {confirmKey && (
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                    onClick={() => setConfirmKey(null)}
                >
                    <div
                        className="w-full max-w-md rounded-xl border border-white/10 p-6"
                        style={{ backgroundColor: "#111111" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold text-white mb-2">Conferma flip critico</h3>
                        <p className="text-sm text-[#CCCCCC] mb-4">
                            Stai per impostare <code className="text-white">{confirmKey.row.key}</code> a{" "}
                            <span style={{ color: "#FFFFFF" }}>{String(confirmKey.newValue)}</span>.
                            Questa è una kill-switch critica che impatta il traffico live.
                        </p>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                onClick={() => setConfirmKey(null)}
                                className="px-4 py-2 rounded-lg text-sm text-white hover:bg-white/[0.06]"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={() => doToggle(confirmKey.row, confirmKey.newValue)}
                                disabled={savingKey === confirmKey.row.key}
                                data-testid="admin-system-settings-confirm"
                                className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                                style={{ backgroundColor: "#FFFFFF" }}
                            >
                                {savingKey === confirmKey.row.key ? "In corso…" : "Conferma flip"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
